import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import type { TransmissionStrategySchema } from '@/services/api/generated/treatmentRecordRequestsService';
import type {
  HipaaPackageSchema,
  PackageValidationResponse,
} from '@/services/api/hipaaPackageService';
import {
  usePublishPackageMutation,
  useRebuildPackagePdfMutation,
  useRenderPackageMutation,
  useValidateAllComponentsMutation,
} from '@/services/api/hipaaPackageService';
import { useCreateTaskUpdateMutation } from '@/services/api/tasksService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { TaskUpdateVisibility } from '@/services/types/client-intake-types';

import { useRequestPage } from '../contexts/RequestPageContext';
import type { ReviewSubmitFormState } from '../types';
import {
  hasBlockingMissingFields,
  hasBlockingValidationMissingFields,
  hasManualPackageContent,
  isPackageRendering,
} from './utils';

type SubmitPhase = 'idle' | 'finalizing' | 'submitting' | 'downloading';

const PACKAGE_RENDER_POLL_INTERVAL_MS = 1000;
const PACKAGE_RENDER_TIMEOUT_MS = 60000;

const sleep = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const buildPackageFilename = (
  providerName: string | undefined,
  extension: 'pdf' | 'docx',
) => {
  const parts = ['HIPAA_Authorization'];
  if (providerName) {
    parts.push(providerName.replace(/[^a-zA-Z0-9]/g, '_'));
  }
  parts.push(new Date().toISOString().split('T')[0]);
  return `${parts.join('_')}.${extension}`;
};

const downloadFile = async (
  url: string,
  providerName: string | undefined,
  extension: 'pdf' | 'docx',
) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Download failed');
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = buildPackageFilename(providerName, extension);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const useReviewSubmitActions = () => {
  const {
    matterId,
    providerId,
    requestId,
    requestInfo,
    request,
    provider,
    packageData,
    refetchRequest,
    refetchPackage,
    closeRequestPage,
  } = useRequestPage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle');

  const [configureStrategy] =
    treatmentRecordRequestService.useTransmissionStrategy2Mutation();
  const [submitHipaaPackage] =
    treatmentRecordRequestService.useSubmitHipaaPackageMutation();
  const [changeStatus] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChangeStatusCreateMutation();
  const [rebuildPackagePdf] = useRebuildPackagePdfMutation();
  const [renderPackage] = useRenderPackageMutation();
  const [publishPackage] = usePublishPackageMutation();
  const [validateAllComponents] = useValidateAllComponentsMutation();
  const [createTaskUpdate] = useCreateTaskUpdateMutation();

  const persistStrategy = useCallback(
    async (formState: ReviewSubmitFormState, forceDisabled = false) => {
      const strategyPayload: TransmissionStrategySchema = {
        enabled: forceDisabled ? false : formState.followUpStrategy.enabled,
        channel: formState.followUpStrategy.channel,
        interval_days: formState.followUpStrategy.intervalDays,
        max_attempts: formState.followUpStrategy.maxAttempts,
      };

      await configureStrategy({
        matterId,
        providerId,
        requestId,
        transmissionStrategySchema: strategyPayload,
      }).unwrap();
    },
    [configureStrategy, matterId, providerId, requestId],
  );

  const createProgressUpdate = useCallback(
    async (text: string) => {
      if (!request?.task_id || !text.trim()) {
        return;
      }

      await createTaskUpdate({
        matterId,
        taskId: request.task_id,
        update: {
          update: text.trim(),
          visibility: TaskUpdateVisibility.PRIVATE,
        },
      }).unwrap();
    },
    [createTaskUpdate, matterId, request?.task_id],
  );

  const ensureFinalPackagePdf = useCallback(async () => {
    if (packageData?.package_source === 'manually_uploaded') {
      return packageData;
    }

    const rebuiltPackage = await rebuildPackagePdf({
      matterId,
      providerId,
      requestId,
    }).unwrap();

    return rebuiltPackage;
  }, [
    matterId,
    packageData,
    providerId,
    rebuildPackagePdf,
    requestId,
  ]);

  const refreshPackageSnapshot = useCallback(async () => {
    const result = await refetchPackage();
    return (result.data as HipaaPackageSchema | undefined) ?? packageData;
  }, [packageData, refetchPackage]);

  const pollUntilPackageSettles = useCallback(
    async (startedAt: number): Promise<HipaaPackageSchema | undefined> => {
      const latestPackage = await refreshPackageSnapshot();

      if (!isPackageRendering(latestPackage)) {
        return latestPackage;
      }

      if (Date.now() - startedAt >= PACKAGE_RENDER_TIMEOUT_MS) {
        throw new Error('Timed out while preparing the package');
      }

      await sleep(PACKAGE_RENDER_POLL_INTERVAL_MS);
      return pollUntilPackageSettles(startedAt);
    },
    [refreshPackageSnapshot],
  );

  const waitForPackageToFinishRendering = useCallback(
    () => pollUntilPackageSettles(Date.now()),
    [pollUntilPackageSettles],
  );

  const validateCurrentPackage = useCallback(async () => {
    if (!packageData?.id) {
      return null;
    }

    const validation = await validateAllComponents({
      ...requestInfo,
      packageId: packageData.id,
    }).unwrap();

    await refreshPackageSnapshot();
    return validation;
  }, [
    packageData?.id,
    refreshPackageSnapshot,
    requestInfo,
    validateAllComponents,
  ]);

  const prepareGeneratedPackageForSubmit = useCallback(async () => {
    const validation = await validateCurrentPackage();
    if (
      validation &&
      hasBlockingValidationMissingFields(
        (validation as PackageValidationResponse).components,
      )
    ) {
      throw new Error('Resolve missing package information before submitting');
    }

    const latestPackage = await refreshPackageSnapshot();
    if (!latestPackage) {
      throw new Error('Package is not ready to submit');
    }

    if (hasBlockingMissingFields(latestPackage.components)) {
      throw new Error('Resolve missing package information before submitting');
    }

    if (latestPackage.status === 'published') {
      return latestPackage;
    }

    if (hasManualPackageContent(latestPackage.components)) {
      return publishPackage(requestInfo).unwrap();
    }

    await renderPackage(requestInfo).unwrap();
    const renderedPackage = await waitForPackageToFinishRendering();

    if (
      renderedPackage?.status !== 'ready' &&
      renderedPackage?.status !== 'published'
    ) {
      throw new Error('Package is not ready to submit');
    }

    if (hasBlockingMissingFields(renderedPackage?.components)) {
      throw new Error('Resolve missing package information before submitting');
    }

    if (renderedPackage.status === 'published') {
      return renderedPackage;
    }

    return publishPackage(requestInfo).unwrap();
  }, [
    publishPackage,
    refreshPackageSnapshot,
    renderPackage,
    requestInfo,
    validateCurrentPackage,
    waitForPackageToFinishRendering,
  ]);

  const handleSubmit = useCallback(
    async (
      formState: ReviewSubmitFormState,
      options?: {
        submitPayload?: Record<string, unknown>;
      },
    ) => {
      if (!request?.channel?.type) {
        toast.error('Please configure a delivery method first');
        return false;
      }

      setIsSubmitting(true);
      setSubmitPhase('submitting');

      try {
        await persistStrategy(formState);

        if (packageData?.package_source === 'generated') {
          setSubmitPhase('finalizing');
          await prepareGeneratedPackageForSubmit();
        } else if (
          packageData?.package_source === 'manually_uploaded' &&
          packageData.status !== 'published'
        ) {
          setSubmitPhase('finalizing');
          await publishPackage(requestInfo).unwrap();
        }

        setSubmitPhase('submitting');

        await submitHipaaPackage({
          matterId,
          providerId,
          requestId,
          submitHipaaPackageSchema: {
            channel_type: request.channel.type,
            ...options?.submitPayload,
          },
        }).unwrap();

        await changeStatus({
          matterId,
          providerId,
          requestId,
          changeTreatmentRecordRequestStatusSchema: {
            new_status: 'request_submitted',
          },
        }).unwrap();

        if (
          formState.postTaskUpdate.enabled &&
          formState.postTaskUpdate.text.trim() &&
          request.task_id
        ) {
          await createProgressUpdate(formState.postTaskUpdate.text);
        }

        toast.success(
          `Request submitted via ${request.channel.type.toLowerCase()}`,
        );

        await Promise.all([refetchRequest(), refetchPackage()]);
        closeRequestPage();
        return true;
      } catch (error) {
        const apiError = error as { data?: { detail?: string } };
        toast.error(apiError?.data?.detail || 'Failed to submit request');
        return false;
      } finally {
        setIsSubmitting(false);
        setSubmitPhase('idle');
      }
    },
    [
      changeStatus,
      createProgressUpdate,
      matterId,
      packageData?.package_source,
      packageData?.status,
      prepareGeneratedPackageForSubmit,
      publishPackage,
      persistStrategy,
      providerId,
      requestInfo,
      refetchPackage,
      refetchRequest,
      request,
      requestId,
      submitHipaaPackage,
      closeRequestPage,
    ],
  );

  const handleDownloadAuthorization = useCallback(
    async (
      formState: ReviewSubmitFormState,
      options: { moveForward: boolean },
    ) => {
      if (!request?.channel?.type) {
        toast.error('Please configure a delivery method first');
        return;
      }

      setIsSubmitting(true);
      setSubmitPhase('downloading');

      try {
        await persistStrategy(formState, true);

        let finalPackage = packageData;
        if (packageData?.package_source === 'generated') {
          setSubmitPhase('finalizing');
          finalPackage = await ensureFinalPackagePdf();
        }

        const latestPackage = finalPackage ?? packageData;
        const downloadUrl =
          latestPackage?.rendered_pdf_url || latestPackage?.download_url;

        if (!downloadUrl) {
          throw new Error('Download not available');
        }

        setSubmitPhase('downloading');
        await downloadFile(downloadUrl, provider?.name ?? undefined, 'pdf');

        if (options.moveForward) {
          await changeStatus({
            matterId,
            providerId,
            requestId,
            changeTreatmentRecordRequestStatusSchema: {
              new_status: 'request_submitted',
            },
          }).unwrap();

          const defaultUpdateText =
            request.channel.type === 'portal'
              ? 'Downloaded HIPAA authorization PDF for portal submission and moved the request forward.'
              : 'Downloaded HIPAA authorization PDF for mailing and moved the request forward.';

          await createProgressUpdate(
            formState.postTaskUpdate.enabled &&
              formState.postTaskUpdate.text.trim()
              ? formState.postTaskUpdate.text
              : defaultUpdateText,
          );

          toast.success('Authorization downloaded and request advanced');
          await Promise.all([refetchRequest(), refetchPackage()]);
          closeRequestPage();
          return;
        }

        toast.success('Authorization PDF downloaded');
      } catch (error) {
        const apiError = error as { data?: { detail?: string } };
        toast.error(
          apiError?.data?.detail || 'Failed to download authorization',
        );
      } finally {
        setIsSubmitting(false);
        setSubmitPhase('idle');
      }
    },
    [
      changeStatus,
      createProgressUpdate,
      ensureFinalPackagePdf,
      matterId,
      packageData,
      persistStrategy,
      provider?.name,
      providerId,
      refetchPackage,
      refetchRequest,
      request,
      requestId,
      closeRequestPage,
    ],
  );

  return {
    ensureFinalPackagePdf,
    handleSubmit,
    handleDownloadAuthorization,
    isSubmitting,
    submitPhase,
  };
};
